import React, { FunctionComponent, ReactNode, useContext, useMemo } from 'react';
import DataRecordTable, { CellTypeEnum, DataRecordTableCell } from 'src/libraries/data-record-table';
import Translate from 'ui/atoms/translate';
import {
  AdminApi,
  AdminProductListing,
  AdminProductsListOrderingEnum,
  AdminProductsListProductTypeEnum,
  ProductStatusEnum,
  AdminProductsValuesRetrieveFieldEnum,
} from 'api';
import { camelCase } from 'change-case';
import StatusTag from 'src/ui/atoms/status-tag';
import WithDataRecord from 'hoc/WithDataRecord';
import Number from 'ui/atoms/number';
import { getProductStatusVariant } from 'ui/helper/variant';
import { I18NContext } from 'src/ui/i18n/provider';
import useApiCall from 'src/hooks/use-api-call';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';

export interface ProductsTableProps {
  defaultLimit: number;
  forceDataLoad?: boolean;
  setForceDataLoad?: (val: boolean) => void;
  goToDetails: (productId: string) => void;
  title?: ReactNode;
  hasPermission: boolean;
  setFilterPopupHeight?: (height: number) => void;
  issuerId?: string;
}

const ProductsTable: FunctionComponent<ProductsTableProps> = WithDataRecord<AdminApi, AdminProductListing>(AdminApi)(
  ({
    data,
    paginationProps,
    setFilterPopupHeight,
    goToDetails,
    title,
    loading,
    hasPermission,
    ordering,
    onOrderBy,
    issuerId,
  }) => {
    const { activeLocale } = useContext(I18NContext);

    const { makeAuthenticatedApi } = useApiCall();
    const translate = useTranslateWithStringArgs();
    const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

    const productTypesFilterOptions = useMemo(() => {
      return Object.values(AdminProductsListProductTypeEnum).map((productType) => ({
        id: productType,
        name: translate(`productType.${camelCase(productType || '')}`),
      }));
    }, [translate]);

    const productStatusFilterOptions = useMemo(() => {
      return Object.values(ProductStatusEnum).map((status) => ({
        id: status,
        name: translate(`issuerProducts.status.${snakeToCamelCaseWithNumbers(status)}`),
      }));
    }, [translate]);

    const cells: Array<DataRecordTableCell<AdminProductListing>> = [
      {
        title: <Translate name={`issuerProducts.issuerProductsTable.productName`} />,
        orderingFieldName: AdminProductsListOrderingEnum.NAME_ASC,
        render: ({ name }) => name,
        type: CellTypeEnum.text,
        filterName: 'name',
        searchFieldName: AdminProductsValuesRetrieveFieldEnum.NAME,
        getFilterData: (params: any) =>
          adminApi.adminProductsValuesRetrieve({ ...params, ...(issuerId && { issuerId: [issuerId] }) }),
      },
      {
        title: <Translate name={`issuerProducts.issuerProductsTable.productType`} />,
        orderingFieldName: AdminProductsListOrderingEnum.PRODUCT_TYPE_ASC,
        render: ({ productType }) => <Translate name={`productType.${camelCase(productType || '')}`} />,
        type: CellTypeEnum.text,
        filterName: 'productType',
        getFilterData: () => {
          return productTypesFilterOptions;
        },
      },
      {
        title: <Translate name={`issuerProducts.issuerProductsTable.issuerName`} />,
        orderingFieldName: AdminProductsListOrderingEnum.ISSUER_NAME_ASC,
        render: ({ issuerName }) => issuerName,
        type: CellTypeEnum.text,
        filterName: 'issuerName',
        searchFieldName: AdminProductsValuesRetrieveFieldEnum.ISSUER_NAME,
        getFilterData: (params: any) =>
          adminApi.adminProductsValuesRetrieve({ ...params, ...(issuerId && { issuerId: [issuerId] }) }),
      },
      {
        title: <Translate name={`issuerProducts.issuerProductsTable.totalVolume`} />,
        orderingFieldName: AdminProductsListOrderingEnum.MAX_ISSUABLE_AMOUNT_ASC,
        alignContent: 'right',
        render: ({ maxIssuableAmount }) => <Number>{maxIssuableAmount}</Number>,
        type: CellTypeEnum.number,
        filterName: 'totalVolume',
      },
      {
        title: <Translate name={`issuerProducts.issuerProductsTable.status`} />,
        orderingFieldName: AdminProductsListOrderingEnum.STATUS_ASC,
        type: CellTypeEnum.text,
        filterName: 'status',
        getFilterData: () => {
          return productStatusFilterOptions;
        },
        render: ({ status }) => (
          <StatusTag variant={getProductStatusVariant(status)} truncate={true}>
            <Translate name={`issuerProducts.status.${status}`} />
          </StatusTag>
        ),
        width: activeLocale === 'de' ? 202 : 174,
      },
    ];

    return (
      <>
        <DataRecordTable
          onOrderBy={onOrderBy}
          ordering={ordering}
          data={data}
          title={title}
          loading={loading}
          paginationProps={paginationProps}
          hasPermission={hasPermission}
          onGoToDetails={goToDetails}
          setFilterPopupHeight={setFilterPopupHeight}
          cells={cells}
        />
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    const { issuerId, ...remainingProps } = props;

    return api.adminProductsList({
      offset,
      limit,
      ordering: ordering as AdminProductsListOrderingEnum,
      ...(props.issuerId && { issuerId: [props.issuerId] }),
      ...remainingProps,
    });
  },
);

export default ProductsTable;
